const Concept = () => {
    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 lg:p-8 space-y-8">
            {/* Header */}
            <div className="text-center mb-8">
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 font-mono mb-2">
                    Our Vision & Approach
                </h1>
                <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
                <p className="text-gray-600 mt-4 text-lg">
                    Advanced pronunciation analysis through multiple assessment technologies
                </p>
            </div>

            {/* Problem 1 Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-blue-500 pl-4">
                    Challenge 1: Limited Language Exposure
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border border-red-200">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center font-bold mr-3">!</div>
                            <h3 className="text-lg font-semibold text-gray-800">The Problem</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                            Language learners often have limited exposure to authentic spoken language outside 
                            the classroom. This restricted contact with native pronunciation reduces opportunities 
                            to internalize correct speech patterns and develop phonetic awareness.
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-3">✓</div>
                            <h3 className="text-lg font-semibold text-gray-800">Our Solution</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                            We leverage audiobooks, podcasts, and authentic audio materials to create an 
                            immersive learning environment. This natural exposure keeps pronunciation 
                            training engaging while providing consistent, real-world language input.
                        </p>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-start">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-3 mt-1">🎯</div>
                        <p className="text-gray-700 flex-1">
                            <span className="font-semibold">Key Insight:</span> Pronunciation mastery requires time and consistency. 
                            Authentic audio materials transform repetitive practice into an engaging, 
                            naturally immersive experience.
                        </p>
                    </div>
                </div>
            </section>

            {/* Problem 2 Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-purple-500 pl-4">
                    Challenge 2: Comprehensive Pronunciation Analysis
                </h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-200">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold mr-3">!</div>
                            <h3 className="text-lg font-semibold text-gray-800">The Challenge</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                            Pronunciation assessment requires analyzing multiple dimensions: phoneme accuracy, 
                            pitch contours, intonation patterns, and rhythm. Single-metric approaches often 
                            miss crucial aspects of speech quality and naturalness.
                        </p>
                    </div>

                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-200">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold mr-3">🔬</div>
                            <h3 className="text-lg font-semibold text-gray-800">Multi-Technology Solution</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">
                            We combine Microsoft Pronunciation Assessment with our proprietary pitch analysis 
                            tools and Praat-inspired visualizations. This multi-layered approach provides 
                            comprehensive feedback on both segmental and suprasegmental features.
                        </p>
                    </div>
                </div>
            </section>

            {/* Technology Stack Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-green-500 pl-4">
                    Our Assessment Technology Stack
                </h2>
                
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-xl p-6 border border-blue-200 text-center">
                        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">🤖</div>
                        <h4 className="font-semibold text-gray-800 mb-3">AI Phoneme Assessment</h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Microsoft's advanced speech recognition for precise phoneme-level accuracy scoring
                        </p>
                        <div className="text-xs text-blue-600 font-medium">
                            Word & Phoneme Accuracy
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-200 text-center">
                        <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">📊</div>
                        <h4 className="font-semibold text-gray-800 mb-3">Pitch & Intonation Analysis</h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Our proprietary tools for analyzing pitch contours and intonation patterns
                        </p>
                        <div className="text-xs text-purple-600 font-medium">
                            Prosody & Melody
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 text-center">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">👁️</div>
                        <h4 className="font-semibold text-gray-800 mb-3">Praat Visualization</h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Professional speech analysis visualizations inspired by Praat software
                        </p>
                        <div className="text-xs text-green-600 font-medium">
                            Spectral Analysis
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
                    <h4 className="font-semibold text-gray-800 mb-4 text-center">Integrated Analysis Approach</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border">
                            <h5 className="font-semibold text-gray-800 mb-2">Segmental Features</h5>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Individual phoneme accuracy</li>
                                <li>• Vowel and consonant quality</li>
                                <li>• Word-level pronunciation</li>
                            </ul>
                        </div>
                        <div className="bg-white p-4 rounded-lg border">
                            <h5 className="font-semibold text-gray-800 mb-2">Suprasegmental Features</h5>
                            <ul className="text-sm text-gray-600 space-y-1">
                                <li>• Pitch contours and intonation</li>
                                <li>• Speech rhythm and timing</li>
                                <li>• Stress patterns</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Combined Approach Section */}
            <section className="space-y-6">
                <h2 className="text-xl lg:text-2xl font-semibold text-gray-800 font-mono border-l-4 border-yellow-500 pl-4">
                    Complete Pronunciation Learning System
                </h2>
                
                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 border border-blue-200">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-2">🎧</div>
                            <h4 className="font-semibold text-gray-800 text-sm">Authentic Input</h4>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-2">🤖</div>
                            <h4 className="font-semibold text-gray-800 text-sm">AI Assessment</h4>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-2">📊</div>
                            <h4 className="font-semibold text-gray-800 text-sm">Pitch Analysis</h4>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                            <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-white text-lg font-bold mx-auto mb-2">👁️</div>
                            <h4 className="font-semibold text-gray-800 text-sm">Praat Viz</h4>
                        </div>
                    </div>

                    <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
                        <h4 className="font-semibold text-gray-800 mb-3 text-center">Comprehensive Feedback</h4>
                        <p className="text-gray-600 text-center text-sm">
                            Our integrated approach provides learners with multi-dimensional feedback, 
                            covering everything from individual sound accuracy to overall speech melody 
                            and naturalness—delivering insights previously available only in professional 
                            speech therapy and linguistics research settings.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default Concept;